<template>
  <div>
    <input
      :style="{ display: 'none' }"
      ref="input"
      type="file"
      accept="image/jpeg,image/jpg,image/png"
      @change="onFileChange"
    />

    <el-dialog
      :class="{
        'imagePicker-el-dialog': true,
        'imagePicker-round-shape': roundShape
      }"
      :width="width"
      :visible.sync="dialogVisible"
      top="5vh"
      :close-on-click-modal="false"
      :modal-append-to-body="modalAppendToBody"
      :append-to-body="true"
      @opened="onDialogOpened"
      @closed="onDialogClosed"
    >
      <div class="dialog-title" slot="title">
        <span>{{ title }}</span>
        <span v-if="hintText">{{ hintText }}</span>
      </div>

      <div class="img-wrapper" :style="{
        height: hintText ? '240px' : '260px' 
      }">
        <img class="img-wrapper__inner" ref="img" :src="imgSrc" alt="" />
      </div>
      <div class="slider">
        <span class="slider__control" @click="zoomValue -= zoomStep">
          <svg-icon style="transform: rotate(90deg);" name="dialog-minus" />
        </span>
        <el-slider
          class="slider__inner"
          :show-tooltip="false"
          v-model="zoomValue"
          :max="zoomMax"
          :min="zoomMin"
          :step="zoomStep"
        />
        <span class="slider__control" @click="zoomValue += zoomStep">
          <svg-icon name="dialog-plus" />
        </span>
      </div>
      <span slot="footer">
        <common-button type="text" @click="handleCancel">{{ canceledText.toUpperCase() }}</common-button>
        <common-button :loading="load || (customUpload && loading)" type="primary" @click="onApply">{{ savedText.toUpperCase() }}</common-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import "cropperjs/dist/cropper.min.css";
import Cropper from "cropperjs";
import { uploadImage } from "@/api/cms";

export default {
  name: 'commonImagePicker',
  data() {
    return {
      imgSrc: null,

      zoomMax: 1,
      zoomMin: -1,
      zoomValue: 0,
      zoomStep: 0.1,
      cropper: null,
      dialogVisible: false,
      load: false,
    }
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    hintText: {
      type: String,
      default: ''
    },
    canceledText: {
      type: String,
      default: 'Cancel'
    },
    savedText: {
      type: String,
      default: 'Apply'
    },
    options: {
      type: Object,
      default: () => {}
    },
    visible: {
      type: Boolean,
      default: false,
    },
    customUpload: { // 自定义上传
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: "400px"
    },
    modalAppendToBody: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false
    },
    roundShape: {
      type: Boolean,
      default: false,
    }
  },
  watch: {
    zoomValue(next, prev) {
      this.cropper.zoom(next - prev);
    },
    visible(next) {
      if (next) {
        this.pickImg();
      }
    }
  },
  methods: {
    pickImg() {
      const input = this.$refs.input;
      input.click();
      this.$emit('update:visible', false);
      this.$emit('close');
    },
    onDialogOpened() {
      this.cropper = new Cropper(this.$refs.img, Object.assign({
        zoomOnTouch: false, // 通过拖动触摸放大图像
        zoomOnWheel: false, // 通过移动鼠标放大图像
        dragMode: 'crop',
        aspectRatio: NaN,
      }, this.options));
    },
    onDialogClosed() {
      if (this.cropper) {
        this.cropper.destroy();
      }
    },
    handleCancel() {
      this.$emit('update:visible', false);
      this.$emit('close');
      this.dialogVisible = false;
    },
    onFileChange(e) {
      const file = e.target.files[0];
      this.$refs.input.value = "";

      // limit size
      if (file.size / 1024 / 1024 > 1) {
        this.$message.error("Failed to upload, The maximum size of the image is 1MB");
        return;
      }

      const reader = new FileReader();
      reader.addEventListener("load", () => {
        this.imgSrc = reader.result;
        this.dialogVisible = true;
      });
      reader.readAsDataURL(file);
    },
    onApply() {
      if (!this.customUpload) {
        this.load = true;
      }
      const canvas = this.cropper.getCroppedCanvas();
      canvas.toBlob(blob => {
        this.$emit("confirm", blob);
        
        if (this.customUpload) return;
        uploadImage(blob)
          .then(({ data }) => {
            this.$emit('uploadSuccess', data.data.url);
            this.handleCancel();
          }).catch(() => {
            this.$emit('uploadFailed')
          }).finally(() => {
            this.load = false;
          });
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.img-wrapper {
  &__inner {
    display: none;
  }
}
.slider {
  height: 68px;
  padding: 24px 32px;
  display: flex;
  align-items: center;
  &__control {
    cursor: pointer;
    font-size: 16px;
    color: #193154;

    &:nth-of-type(1) {
      margin-right: 12px;
    }

    &:nth-of-type(2) {
      margin-left: 12px;
    }
  }
  &__inner {
    flex-grow: 1;
  }
}
</style>

<style lang="scss">
.imagePicker-el-dialog {
  .el-dialog {
    margin: 0 !important;
    border-radius: 6px;
    overflow: hidden;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    .el-icon-close{
      font-size: 26px;
    }
    &__header {
      padding: 32px 32px 24px;
      background: #FFF;

      &btn {
        top: 30px;
      }

      .dialog-title {
        display: flex;
        flex-direction: column;

        -webkit-user-select: none; /* Safari */
        -ms-user-select: none; /* IE 10+ and Edge */
        user-select: none; /* Standard syntax */

        span {
          font-weight: normal;
          font-weight: normal;
          font-size: 12px;
          color: #7f8596;

          &:nth-of-type(1) {
            font-weight: bold;
            font-weight: bold;
            color: #000;
            font-size: 16px;
            height: 22px;
          }

          &:nth-of-type(2) {
            margin-top: 4px;
          }
        }
      }
    }

    &__body {
      padding: 0 !important;
    }

    &__footer {
      padding: 0 32px 32px;
      border: none;

      button:nth-of-type(2) {
        margin-left: 16px;
      }
    }
  }
}

.imagePicker-round-shape {
  .cropper-face {
    border-radius: 50%;
  }
  .cropper-view-box {
    border-radius: 50%;
  }
}
</style>
